import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TenantSetting } from "../domain/TenantSlice/TenantSetting";
import { TenantSettingsRepository } from "../infrastructure/TenantsSlice/TenantSettingsRepository";
import { TenantFeatureFlagUpdate } from "../domain/TenantSlice/TenantFeatureFlagUpdate";
import { TenantGlobalTaxUpdate } from "../domain/TenantSlice/TenantGlobalTaxUpdate";
import { StoreStatus, StoreStatusType } from "./StoreStatus";
import { initialStoreStateBase, StoreStateBase } from "./StoreStateBase";
import type { RootState, AppDispatch } from "./index";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";

export interface TenantSettingState extends StoreStateBase {
  entity: TenantSetting | undefined;
}

const initialState: TenantSettingState = {
  ...initialStoreStateBase,
  entity: undefined,
};

const repo = new TenantSettingsRepository();

export const fetchTenantSetting = createAsyncThunk<
  TenantSetting | null,
  void,
  { state: RootState }
>("tenantSetting/fetch", async (_, { getState }) => {
  const current = getState().tenantSetting?.entity;
  if (current) {
    return current;
  }
  return await repo.fetchTenantSettings();
});

export const updateTenantFeatureFlag = createAsyncThunk<
  TenantFeatureFlagUpdate,
  TenantFeatureFlagUpdate,
  { state: RootState }
>("tenantSetting/updateFeatureFlag", async (update) => {
  await repo.updateFeatureFlag(update);
  return update;
});

export const updateTenantGlobalTax = createAsyncThunk<
  TenantGlobalTaxUpdate,
  TenantGlobalTaxUpdate,
  { state: RootState }
>("tenantSetting/updateGlobalTax", async (dto) => {
  await repo.updateGlobalTax(dto);
  return dto;
});

const tenantSettingSlice = createSlice({
  name: "tenantSetting",
  initialState,
  reducers: {
    setTenantSetting(state, action: PayloadAction<TenantSetting | null>) {
      state.entity = action.payload ?? undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTenantSetting.pending, (state) => {
        state.storeStatus = StoreStatus.loading as StoreStatusType;
        state.error = null;
      })
      .addCase(fetchTenantSetting.fulfilled, (state, action) => {
        state.storeStatus = StoreStatus.succeeded as StoreStatusType;
        state.entity = action.payload ?? undefined;
      })
      .addCase(fetchTenantSetting.rejected, (state, action) => {
        state.storeStatus = StoreStatus.failed as StoreStatusType;
        state.error = action.error.message ?? "Error loading tenant settings";
      })
      .addCase(updateTenantFeatureFlag.fulfilled, (state, action) => {
        if (!state.entity) return;
        const { tenantFeatureFlagId, isEnabled } = action.payload;
        const target = state.entity.tenantFeatureFlags.find(
          (f) => f.tenantFeatureFlagId === tenantFeatureFlagId
        );
        if (target) {
          target.isEnabled = isEnabled;
        }
      })
      .addCase(updateTenantGlobalTax.fulfilled, (state, action) => {
        if (!state.entity) return;
        state.entity.isTaxEnabled = action.payload.isTaxEnabled;
        state.entity.globalTaxPercentage = action.payload.globalTaxPercentage;
      });
  },
});

export const { setTenantSetting } = tenantSettingSlice.actions;
export const tenantSettingReducer = tenantSettingSlice.reducer;

export const selectTenantSetting = (state: RootState) =>
  state.tenantSetting.entity;

export const selectTenantSettingStatus = (state: RootState) =>
  state.tenantSetting.storeStatus ?? StoreStatus.idle;

// Facade hook following app conventions
export function useTenantSettingStore() {
  const dispatch = useDispatch<AppDispatch>();
  const tenantSettings = useSelector(selectTenantSetting);
  const status = useSelector(selectTenantSettingStatus);

  useEffect(() => {
    if (!tenantSettings && status !== StoreStatus.loading) {
      dispatch(fetchTenantSetting());
    }
  }, [tenantSettings, status, dispatch]);

  return {
    tenantSettings,
    status,
    updateFeatureFlag: (update: TenantFeatureFlagUpdate) =>
      dispatch(updateTenantFeatureFlag(update)),
    updateGlobalTax: (dto: TenantGlobalTaxUpdate) =>
      dispatch(updateTenantGlobalTax(dto)),
  } as const;
}
